import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  KeyboardArrowRight,
  KeyboardArrowDown,
  Download,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  getEvaluationStatusText,
  getStatusBackgroundColor,
} from "../../../Helpers/evaluationHelpers";
import { userEvaluationRequests } from "../../../Models/userEvaluationRequests";
import { getEntityIconByName } from "../../../Helpers/getEntityIcon";
import { entityIcons } from "../../../Constants/entityIcons";
import LogCardDetails from "./LogCardDetails";
import {
  useDownloadEvaluation,
  useDownloadEvaluationDocument,
} from "../../../Query/useEvaluation";
import { downloadDocumentBlob } from "../../../Helpers/documentHelper";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface LogCardProps {
  evaluationRequest: userEvaluationRequests;
  showUserInfo?: boolean;
}

const LogCard: React.FC<LogCardProps> = ({
  evaluationRequest,
  showUserInfo = true,
}) => {
  const { t } = useTranslation("actionLog");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const isAlive = evaluationRequest.is_alive;
  const downloadEvaluationMutation = useDownloadEvaluation();
  const navigate = useNavigate();
  const { mutate: downloadEvaluationDocument } =
    useDownloadEvaluationDocument();

  const cardStyles = {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "flex-start" : "center",
    padding: 2,
    backgroundColor: color.white,
    gap: isMobile ? 1.5 : 2,
    width: "100%",
    flexWrap: "nowrap",
    borderRadius: expanded ? 0 : 2,
  };

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleDownloadEvaluationResults = async () => {
    handleDownloadMenuClose();
    downloadEvaluationMutation.mutate(evaluationRequest.id.toString(), {
      onSuccess: (response) => {
        downloadDocumentBlob(response, evaluationRequest.id.toString());
      },
    });
  };

  const handleDownloadQAInsights = () => {
    handleDownloadMenuClose();
    downloadEvaluationDocument(
      { evaluationId: evaluationRequest.evaluation_id },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(
            response,
            `${evaluationRequest.evaluation_id}.docx`,
          );
        },
      },
    );
  };

  const handleUserClick = () => {
    navigate(`/user/${evaluationRequest.user.id}`);
  };

  const evaluatorName = `${evaluationRequest.user.name} ${evaluationRequest.user.surname}`;

  return (
    <Badge
      color="warning"
      badgeContent={t("logs.alive")}
      invisible={!isAlive}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{ display: "inline" }}
    >
      <Box
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          width: "100%",
        }}
      >
        <Box sx={cardStyles}>
          {showUserInfo && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                color: color.primary_dark,
                "&:hover": { color: color.accent },
                minWidth: isMobile ? "100%" : "150px",
              }}
              onClick={handleUserClick}
            >
              <Typography variant="body2" fontWeight="bold" color="inherit">
                {evaluatorName}
              </Typography>
              <Typography variant="caption" color="inherit">
                {evaluationRequest.user.email}
              </Typography>
            </Box>
          )}

          {!isMobile && showUserInfo && (
            <KeyboardArrowRight fontSize="small" sx={{ color: color.accent }} />
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {evaluationRequest.company.company_icon_id &&
              React.cloneElement(
                getEntityIconByName(
                  entityIcons[evaluationRequest.company.company_icon_id - 1]
                    .iconName,
                )!,
                { sx: { color: color.secondary, fontSize: 24 } },
              )}
            <Typography
              variant="body2"
              fontWeight="medium"
              color={color.primary_dark}
            >
              {evaluationRequest.company.company_name}
            </Typography>
            {evaluationRequest.company.workspace_shared && (
              <Chip
                label={t("logs.shared")}
                size="small"
                sx={{
                  backgroundColor: color.accent,
                  color: color.white,
                  ml: 1,
                }}
              />
            )}
            <Chip
              label={evaluationRequest.metadata?.model || "/"}
              variant="outlined"
              size="small"
              sx={{
                borderColor: color.accent,
                color: color.accent,
                fontWeight: "bold",
                ml: 1,
              }}
            />
          </Box>

          {!isMobile && (
            <KeyboardArrowRight fontSize="small" sx={{ color: color.accent }} />
          )}

          <Box sx={{ minWidth: isMobile ? "100%" : "auto" }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              color={color.primary_dark}
            >
              {evaluationRequest.metadata?.question_list_name || "/"}
            </Typography>
            <Typography variant="caption" color={color.secondary}>
              {evaluationRequest.metadata?.certification_category_name}
            </Typography>
          </Box>

          <Typography sx={{ margin: "0 8px" }}>
            <KeyboardArrowRight
              fontSize="medium"
              sx={{ color: color.accent }}
            />
          </Typography>

          <Chip
            label={getEvaluationStatusText(evaluationRequest.evaluation_status)}
            size="small"
            sx={{
              backgroundColor: getStatusBackgroundColor(
                evaluationRequest.evaluation_status,
              ),
              color: color.white,
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="subtitle2"
              marginRight={1}
              color={color.primary_dark}
            >
              {evaluationRequest.created_at}
            </Typography>
            <IconButton
              size="small"
              onClick={handleDownloadMenuOpen}
              sx={{ color: color.primary_dark }}
            >
              <Download fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleExpandClick}>
              <KeyboardArrowDown
                fontSize="small"
                sx={{
                  transition: "transform 0.3s",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <LogCardDetails
        expanded={expanded}
        evaluationRequest={evaluationRequest}
      />

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleDownloadMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: color.white,
            color: color.textPrimary,
            border: `1px solid ${color.gray200}`,
            borderRadius: "10px",
            "& .MuiMenuItem-root": {
              fontSize: { xs: "0.7rem", sm: "0.85rem" },
              bgcolor: "white",
              color: color.textPrimary,
              "&:hover": { bgcolor: color.gray50 },
              "&.Mui-selected": {
                bgcolor: color.gray50,
                "&:hover": { bgcolor: color.gray50 },
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleDownloadEvaluationResults}>
          {t("evaluationHistory")}
        </MenuItem>
        {isAlive && (
          <MenuItem onClick={handleDownloadQAInsights}>
            {t("qaInsights")}
          </MenuItem>
        )}
      </Menu>
    </Badge>
  );
};

export default LogCard;
